import { useState, type ReactNode } from 'react';

import { api, qs } from '../api';
import {
  Aviso,
  Cartao,
  Estado,
  Etiqueta,
  Painel,
  Tempo,
  data,
  useAcao,
  useCarga,
} from '../comum';
import type { Ticket } from '../tipos';

/**
 * Tom do status do chamado.
 *
 * ⚠️ O estado canônico (`aberto`/`parado`/`fechado`) vem do KACE e é ele que
 * governa SLA — o rótulo que o cliente vê é customizável e não serve para
 * decidir cor. Por isso lemos `estado`, e mostramos `statusNome`.
 */
function tomDoEstado(estado: string): 'ok' | 'alerta' | 'neutro' {
  if (estado === 'fechado') return 'ok';
  if (estado === 'parado') return 'alerta';
  return 'neutro';
}

interface ResumoTickets {
  readonly abertos: number;
  readonly parados: number;
  readonly fechados: number;
  readonly semResponsavel: number;
  readonly vencidos?: number;
}

interface PaginaTickets {
  readonly itens: readonly Ticket[];
  readonly total: number;
}

interface GrupoDeCausa {
  readonly causa: string;
  readonly explicacao: string;
  readonly acaoSugerida: string;
  readonly total: number;
  readonly chamados: readonly {
    readonly numero: number;
    readonly titulo: string;
    readonly estado: string;
    readonly fila: string | null;
  }[];
}

interface Padroes {
  readonly analisados: number;
  readonly foraDeGrupo?: number;
  readonly grupos: readonly GrupoDeCausa[];
  readonly motivo?: string;
}

const VAZIO: Padroes = { analisados: 0, grupos: [] };

export function Tickets({
  aoPerderSessao,
  podeTriar,
}: {
  aoPerderSessao: () => void;
  podeTriar: boolean;
}): ReactNode {
  const [busca, setBusca] = useState('');
  const [estado, setEstado] = useState('abertos');
  const [semDono, setSemDono] = useState(false);
  const [verPadroes, setVerPadroes] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const acao = useAcao(aoPerderSessao);

  /**
   * Padrões por causa raiz.
   *
   * Carregado só quando pedido: a análise chama o modelo, que roda em CPU dentro
   * do cliente. Rodar a cada abertura da tela gastaria minutos de processamento
   * para uma informação que ninguém pediu.
   */
  const padroes = useCarga<Padroes>(
    () => (verPadroes ? api.get('/api/chamados/padroes?dias=365') : Promise.resolve(VAZIO)),
    [verPadroes],
    aoPerderSessao,
  );

  const resumo = useCarga<ResumoTickets>(
    () => api.get('/api/tickets/resumo'),
    [],
    aoPerderSessao,
  );
  const lista = useCarga<PaginaTickets>(
    // `estado` só entra se tiver valor: mandar `estado=` vazio já custou caro
    // neste produto.
    () => api.get(`/api/tickets${qs({ busca, estado, semResponsavel: semDono, limite: 200 })}`),
    [busca, estado, semDono],
    aoPerderSessao,
  );

  const triar = (id: string): void => {
    void acao.rodar(async () => {
      const r = await api.post<{ jaExistia?: boolean }>(
        `/api/tickets/${encodeURIComponent(id)}/triagem`,
      );
      return r.jaExistia === true
        ? 'Já havia uma sugestão pendente para este chamado — veja em Aprovações.'
        : 'Triagem gerada. A sugestão está em Aprovações, aguardando decisão humana.';
    });
  };

  return (
    <>
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      {/*
        Padrões por causa raiz.
        Doze chamados que são o mesmo problema não são doze atendimentos — são
        um. Encontrar isso lendo lista é o que ninguém consegue fazer.
      */}
      <div className="linha" style={{ marginBottom: 12 }}>
        <span className="cresce" />
        <button className="encolhe" onClick={() => setVerPadroes((v) => !v)}>
          {verPadroes ? 'Esconder padrões' : '✦ Encontrar padrões nos chamados'}
        </button>
      </div>

      {verPadroes && (
        <Painel
          titulo="Chamados com a mesma causa"
          sub="A IA agrupa; o produto confere cada número contra os chamados que existem."
        >
          <Estado carga={padroes}>
            {padroes.dados !== null && (
              <>
                <p className="suave" style={{ fontSize: 13 }}>
                  {padroes.dados.analisados} chamado(s) analisado(s) no último ano
                  {padroes.dados.foraDeGrupo !== undefined &&
                    ` · ${padroes.dados.foraDeGrupo} fora de qualquer grupo`}
                </p>

                {padroes.dados.motivo !== undefined && (
                  <Aviso>{padroes.dados.motivo}</Aviso>
                )}

                {padroes.dados.grupos.length === 0 &&
                  padroes.dados.motivo === undefined && (
                    <Aviso>
                      Nenhum padrão encontrado. Isso é uma resposta boa: significa que
                      os chamados do período não se repetem.
                    </Aviso>
                  )}

                {padroes.dados.grupos.map((g) => (
                  <div key={g.causa} className="grupo-causa">
                    <h4>
                      {g.causa} <span className="qtd-causa">{g.total} chamados</span>
                    </h4>
                    <p className="suave">{g.explicacao}</p>
                    {g.acaoSugerida !== '' && (
                      <p className="acao-causa">
                        <strong>Resolveria de uma vez:</strong> {g.acaoSugerida}
                      </p>
                    )}
                    <ul className="chamados-do-grupo">
                      {g.chamados.map((c) => (
                        <li key={c.numero}>
                          <b>#{c.numero}</b> {c.titulo}
                          <span className="suave"> · {c.estado}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </>
            )}
          </Estado>
        </Painel>
      )}

      <Estado carga={resumo}>
        {resumo.dados !== null && (
          <div className="cartoes">
            <Cartao rotulo="Abertos" valor={resumo.dados.abertos} />
            <Cartao
              rotulo="Parados"
              valor={resumo.dados.parados}
              nota="aguardando terceiro"
              destaque
              titulo="Chamado parado não conta para o SLA, mas continua sendo problema de alguém."
            />
            <Cartao rotulo="Fechados" valor={resumo.dados.fechados} />
            <Cartao
              rotulo="Sem responsável"
              valor={resumo.dados.semResponsavel}
              nota="ninguém assumiu"
              destaque
              titulo="Chamado aberto sem dono é o que mais vira reclamação."
            />
          </div>
        )}
      </Estado>

      <div className="linha">
        <div>
          <input
            placeholder="Buscar por título, solicitante, número…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="encolhe">
          <select value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="abertos">Abertos e parados</option>
            <option value="aberto">Só abertos</option>
            <option value="parado">Só parados</option>
            <option value="fechado">Fechados</option>
            <option value="todos">Todos</option>
          </select>
        </div>
        <label className="encolhe" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={semDono}
            onChange={(e) => setSemDono(e.target.checked)}
          />
          <span>Sem responsável</span>
        </label>
      </div>

      <Painel>
        <Estado carga={lista}>
          {lista.dados !== null && (
            <div className="rolagem">
              <table className="densa">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Título</th>
                    <th>Status</th>
                    <th>Categoria</th>
                    <th>Responsável</th>
                    <th>Vence</th>
                    <th>Aberto há</th>
                    {podeTriar && <th />}
                  </tr>
                </thead>
                <tbody>
                  {lista.dados.itens.map((t) => (
                    <tr key={t.id}>
                      <td className="mono">{t.numero}</td>
                      <td className="forte">{t.titulo}</td>
                      <td>
                        <Etiqueta tom={tomDoEstado(t.estado)}>
                          {t.statusNome ?? t.estado}
                        </Etiqueta>
                      </td>
                      <td className="suave">{t.categoriaNome ?? '—'}</td>
                      <td className={t.responsavelNome === null ? 'atencao' : 'suave'}>
                        {t.responsavelNome ?? 'sem responsável'}
                      </td>
                      {/*
                        🔴 O prazo do SLA nunca apareceu na tela.
                        O produto construiu um vigia inteiro em cima deste campo
                        e a lista de chamados continuava calada sobre ele.
                      */}
                      <td>
                        {t.venceEm === null ? (
                          <span className="suave">sem prazo</span>
                        ) : (
                          <PrazoSla iso={t.venceEm} fechado={t.estado === 'fechado'} />
                        )}
                      </td>
                      <td>
                        {/*
                          Chamado aberto há 40 dias é o sintoma; a data de
                          abertura sozinha esconde isso numa lista longa. Só
                          alerta para os que ainda estão abertos — fechado
                          antigo é histórico, não pendência.
                        */}
                        <Tempo
                          iso={t.criadoEm}
                          alertarApos={t.estado === 'fechado' ? 100_000 : 15}
                        />
                      </td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button onClick={() => setAberto(t.id)}>Abrir</button>{' '}
                        {podeTriar && (
                          <button disabled={acao.executando} onClick={() => triar(t.id)}>
                            Triar com IA
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Estado>
      </Painel>

      {aberto !== null && (
        <FichaDoChamado
          id={aberto}
          aoFechar={() => setAberto(null)}
          aoPerderSessao={aoPerderSessao}
        />
      )}
    </>
  );
}

/**
 * Quanto falta para o prazo.
 *
 * Vermelho depois de vencido, atenção nas últimas 24 horas. Chamado fechado não
 * mostra urgência: prazo de chamado resolvido é histórico, não pendência — e
 * pintar a lista inteira de vermelho faz ninguém enxergar o que importa.
 */
function PrazoSla({ iso, fechado }: { iso: string; fechado: boolean }): ReactNode {
  const horas = (new Date(iso).getTime() - Date.now()) / 3600_000;
  const texto = data(iso);

  if (fechado) return <span className="suave">{texto}</span>;
  if (horas < 0) {
    return (
      <span className="prazo-vencido" title={texto}>
        venceu há {Math.abs(Math.round(horas))}h
      </span>
    );
  }
  if (horas <= 24) {
    return (
      <span className="prazo-apertado" title={texto}>
        em {Math.max(1, Math.round(horas))}h
      </span>
    );
  }
  return <span className="suave">{texto}</span>;
}

interface Movimentacao {
  readonly id: string;
  readonly autorNome: string | null;
  readonly tipo: string;
  readonly texto: string;
  readonly visivelAoSolicitante: boolean;
  readonly minutos: number | null;
  readonly ocorridoEm: string;
}

interface ChamadoDetalhe extends Ticket {
  readonly descricao: string;
  readonly resolucao: string | null;
  readonly filaNome: string | null;
  readonly impactoNome: string | null;
  readonly solicitanteEmail: string | null;
  readonly responsavelEmail: string | null;
  readonly dispositivoIdNoKace: number | null;
}

/**
 * Ficha do chamado.
 *
 * 🔴 **Não existia.** No Inventário você clica numa máquina e vê a ficha 360; em
 * Chamados você clicava e não acontecia nada. A rota `/api/tickets/:id` estava
 * pronta e nenhuma tela a chamava.
 *
 * Junto vem a **timeline** — as movimentações que o produto sincroniza desde o
 * primeiro dia e nunca mostrou. É o histórico do atendimento: quem falou o quê,
 * quando, e quanto tempo lançou. Sem ela, "chamado parado há 3 dias" é uma
 * afirmação que ninguém consegue conferir.
 */
function FichaDoChamado({
  id,
  aoFechar,
  aoPerderSessao,
}: {
  id: string;
  aoFechar: () => void;
  aoPerderSessao: () => void;
}): ReactNode {
  const carga = useCarga<ChamadoDetalhe>(
    () => api.get(`/api/tickets/${encodeURIComponent(id)}`),
    [id],
    aoPerderSessao,
  );
  const timeline = useCarga<{ itens: readonly Movimentacao[] }>(
    () => api.get(`/api/tickets/${encodeURIComponent(id)}/timeline`),
    [id],
    aoPerderSessao,
  );

  return (
    <div className="gaveta" role="dialog" aria-label="Chamado">
      <header>
        <div>
          <strong>
            {carga.dados === null ? 'Carregando…' : `#${carga.dados.numero}`}
          </strong>
          <span>{carga.dados?.titulo ?? ''}</span>
        </div>
        <button className="icone" onClick={aoFechar} aria-label="Fechar">
          ✕
        </button>
      </header>

      <div className="corpo-gaveta">
        <Estado carga={carga}>
          {carga.dados !== null && (
            <>
              <table>
                <tbody>
                  <LinhaT r="Estado" v={carga.dados.statusNome ?? carga.dados.estado} />
                  <LinhaT r="Fila" v={carga.dados.filaNome} />
                  <LinhaT r="Prioridade" v={carga.dados.prioridadeNome} />
                  <LinhaT r="Impacto" v={carga.dados.impactoNome} />
                  <LinhaT r="Categoria" v={carga.dados.categoriaNome} />
                  <LinhaT
                    r="Solicitante"
                    v={carga.dados.solicitanteNome ?? carga.dados.solicitanteEmail}
                  />
                  <LinhaT
                    r="Responsável"
                    v={carga.dados.responsavelNome ?? '(sem responsável)'}
                  />
                  <LinhaT r="Aberto em" v={data(carga.dados.criadoEm)} />
                  <LinhaT
                    r="Vence em"
                    v={carga.dados.venceEm === null ? 'sem prazo' : data(carga.dados.venceEm)}
                  />
                  <LinhaT
                    r="Fechado em"
                    v={carga.dados.fechadoEm === null ? null : data(carga.dados.fechadoEm)}
                  />
                </tbody>
              </table>

              {carga.dados.descricao !== '' && (
                <>
                  <h3>Descrição</h3>
                  <p className="texto-chamado">{carga.dados.descricao}</p>
                </>
              )}

              {/*
                A resolução escrita é o que alimenta a sugestão da IA. Quando
                falta, o produto não tem de onde tirar resposta — e dizer isso é
                mais útil que esconder o campo vazio.
              */}
              <h3>Resolução</h3>
              {carga.dados.resolucao === null || carga.dados.resolucao === '' ? (
                <p className="suave">
                  Fechado sem resolução escrita. É o que impede a IA de aprender com
                  este chamado.
                </p>
              ) : (
                <p className="texto-chamado">{carga.dados.resolucao}</p>
              )}

              <h3>Histórico</h3>
              <Estado carga={timeline}>
                {timeline.dados !== null &&
                  (timeline.dados.itens.length === 0 ? (
                    <p className="suave">
                      Sem movimentações sincronizadas. O histórico vem aos poucos, no
                      limite configurado por sincronização.
                    </p>
                  ) : (
                    <ol className="timeline">
                      {timeline.dados.itens.map((m) => (
                        <li key={m.id}>
                          <div className="cabeca-mov">
                            <strong>{m.autorNome ?? 'sistema'}</strong>
                            <span className="suave">{m.tipo}</span>
                            {!m.visivelAoSolicitante && (
                              <Etiqueta tom="neutro">interno</Etiqueta>
                            )}
                            {m.minutos !== null && m.minutos > 0 && (
                              <span className="suave">{m.minutos} min</span>
                            )}
                            <span className="cresce" />
                            <span className="suave">{data(m.ocorridoEm)}</span>
                          </div>
                          {m.texto !== '' && <p className="texto-chamado">{m.texto}</p>}
                        </li>
                      ))}
                    </ol>
                  ))}
              </Estado>
            </>
          )}
        </Estado>
      </div>
    </div>
  );
}

const LinhaT = ({ r, v }: { r: string; v: string | null }): ReactNode =>
  v === null || v === '' ? null : (
    <tr>
      <th>{r}</th>
      <td>{v}</td>
    </tr>
  );
